/** Thin fetch wrapper for the Ovce na broju API. Errors carry the server's { error, message } body. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly body: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

export class NetworkError extends Error {}

/**
 * Called after every successful write (POST/PUT/PATCH/DELETE). The query layer registers a listener that marks
 * all cached data stale, so no screen can keep showing numbers a write just changed. This is deliberately
 * independent of the live-update stream, which only has to cover changes made by the other member.
 */
let afterWrite: () => void = () => {};
export const onSuccessfulWrite = (fn: () => void) => {
  afterWrite = fn;
};

export async function api<T = unknown>(
  path: string,
  init: { method?: string; body?: unknown } = {},
): Promise<T> {
  const method = init.method ?? (init.body !== undefined ? 'POST' : 'GET');
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers: init.body !== undefined ? { 'content-type': 'application/json' } : undefined,
      body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
      credentials: 'same-origin',
    });
  } catch (e) {
    throw new NetworkError(e instanceof Error ? e.message : 'Network error');
  }
  const text = await res.text();
  const json = text ? JSON.parse(text) : null;
  if (!res.ok) {
    throw new ApiError(res.status, json?.error ?? 'error', json?.message ?? res.statusText, json ?? {});
  }
  if (method !== 'GET') afterWrite();
  return json as T;
}

export const get = <T>(path: string) => api<T>(path);
export const post = <T = unknown>(path: string, body: unknown = {}) => api<T>(path, { method: 'POST', body });
export const put = <T = unknown>(path: string, body: unknown = {}) => api<T>(path, { method: 'PUT', body });
export const patch = <T = unknown>(path: string, body: unknown = {}) =>
  api<T>(path, { method: 'PATCH', body });
export const del = <T = unknown>(path: string) => api<T>(path, { method: 'DELETE' });

export const errorMessage = (e: unknown) =>
  e instanceof ApiError
    ? e.message
    : e instanceof NetworkError
      ? "You're offline — try again when connected"
      : 'Something went wrong';
